<template>
	<IntersectionRoot
		:threshold="0.75"
		class="message-list"
		ref="list"
		@scroll="handleScroll"
		tabindex="-1"
		:class="{ 'scroll-locked': activeHoverLockId }"
	>
		<div class="loading-skeletons" v-if="isLoading" />
		<div class="jump-to-latest-anchor">
			<JumpToLatest
				v-show="showJumpToLatest"
				@click="jumpToLatest"
				:loading="isJumpingToLatest"
			/>
		</div>
		<MessageListLoader
			v-if="allowLoadForward"
			@click="close"
			@enter="loadForward"
			@leave="loadedForward = false"
			:isLoadingMore="isLoadingMore"
		/>
		<Message
			v-for="(message, index) in list"
			:key="message.key"
			:message="message"
			:previousMessage="list[index + 1]"
			:nextMessage="list[index - 1]"
			:readReceipts="readReceiptsByMessageId[message.id] || []"
			:skipAnimation="skipAnimation"
			:trimActionArea="trimActionsForMsgId === index"
			:hideReplyAction="hideReplyAction"
			:hideForwardAction="hideForwardAction || !!message.systemevent"
			:hidePinAction="hidePinAction"
			:hideThreadLine="hideThreadLine"
			:hideThreadElements="hideThreadElements"
			:readOnly="readOnly"
			:idPrefix="idPrefix"
			:isListVisible="isListVisible"
			:isLastViewedMsg="lastViewedId && lastViewedId === message.id"
			:isPinned="isPinned(message.id)"
			:zIndex="list.length - index"
			:hoverLockId="activeHoverLockId"
			@thread="$emit('thread', message)"
			@reply="$emit('reply', message)"
			@create-thread="$emit('create-thread', message)"
			@image="$emit('image', $event)"
			@video="$emit('video', $event)"
			@edit="onMessageEdit($event, index)"
			@viewed="$emit('viewed-all-unreads', $event)"
			@lock-hover="lockHoverToMessage"
			@clear-hover="clearHoverLock"
			class="no-highlight"
		/>
		<ChatHeaderThread
			v-if="!readOnly && historyComplete && isThread"
			:chatId="chatId"
		/>
		<ChatHeaderGroup
			v-else-if="!readOnly && historyComplete && !isDM"
			:chatId="chatId"
		/>
		<ChatHeaderDM
			v-else-if="!readOnly && historyComplete && isDM"
			:chatId="chatId"
		/>
		<MessageListLoader
			@click="close"
			@enter="loadBackward"
			@leave="loadedBackward = false"
			:isLoadingMore="isLoadingMore"
		/>
		<div v-if="clickToClose" @click="close" class="negative-space" />
	</IntersectionRoot>
</template>

<script>
import store, { getChat } from '@/store'
import Message from '../Message'
import JumpToLatest from './JumpToLatest'
import { IntersectionRoot } from '@/components/common/Intersection'
import debounce from 'debounce'
import { hideAll } from 'tippy.js'
import MessageListLoader from './MessageListLoader'
import { removeDuplicatesByField } from '@/helpers/utils'
import { ChatHeaderGroup, ChatHeaderDM, ChatHeaderThread } from './Header'

export const JUMP_TO_LATEST_TOLERANCE = -30

export default {
	name: 'MessageList',

	props: {
		chatId: { type: Number, default: null },
		clickToClose: { type: Boolean, default: null },
		messages: Array,
		isLoading: { type: Boolean, default: null },
		isLoadingMore: { type: Boolean, default: null },
		readReceiptsByMessageId: {
			type: Object,
			default() {
				return {}
			},
		},
		hideReplyAction: { type: Boolean, default: null },
		hideForwardAction: { type: Boolean, default: null },
		hidePinAction: { type: Boolean, default: null },
		hideThreadLine: { type: Boolean, default: null },
		hideThreadElements: { type: Boolean, default: null },
		readOnly: { type: Boolean, default: false },
		idPrefix: String,
		isListVisible: { type: Boolean, default: null },
		allowLoadForward: { type: Boolean, default: null },
		isJumpingToLatest: { type: Boolean, default: null },
		lastViewedId: [String, Number],
		historyComplete: { type: Boolean, default: null },
	},

	components: {
		Message,
		MessageListLoader,
		JumpToLatest,
		IntersectionRoot,
		ChatHeaderGroup,
		ChatHeaderDM,
		ChatHeaderThread,
	},

	data() {
		return {
			store,
			skipAnimation: true,
			previousMessage: null,
			loadedBackward: false,
			loadedForward: false,
			awayFromBottom: false,
			isEditing: false,
			trimActionsForMsgId: null,
			activeHoverLockId: null,
		}
	},

	emits: [
		'thread',
		'reply',
		'create-thread',
		'image',
		'video',
		'viewed-all-unreads',
		'ref',
		'close',
		'load-backward',
		'load-forward',
		'jump-to-latest',
	],

	mounted() {
		this.$emit('ref', this.$refs.list?.$el)
		this.$refs.list?.$el?.addEventListener('copy', this.onCopy)
	},

	beforeUnmount() {
		this.$refs.list?.$el?.removeEventListener('copy', this.onCopy)
	},

	watch: {
		'store.currentChatId': {
			handler(newId, oldId) {
				// this is the first set of messages for new bubble so don't animate
				if (oldId && oldId !== newId) {
					this.skipAnimation = true
				}
			},
			deep: true,
		},

		messages: {
			handler(msgs, old) {
				const lengthDiff = msgs.length - old.length
				// if the list is increasing in length by 1, 2 or 3, then animate
				// otherwise, skip animation completely
				const isSmallSet = [1, 2, 3].includes(lengthDiff)
				this.skipAnimation = this.isEditing || !isSmallSet

				const { scrollTop } = this.$refs.list.$el
				const withinThreshold = scrollTop > JUMP_TO_LATEST_TOLERANCE
				if (isSmallSet && withinThreshold) {
					// if we're within the threshold, then let's just pin the user to
					// the bottom of the list whenever new messages come in
					// See: https://github.com/Hitlabs/pronto-web/issues/2485
					this.scrollToBottom()
				}
			},
			deep: true,
		},

		isLoadingMore(loading, wasLoading) {
			if (loading) {
				this.skipAnimation = true
			} else if (wasLoading && this.isListVisible) {
				setTimeout(() => {
					// Chrome was having issues with animating
					// on loadMore, so this prevents animations
					this.skipAnimation = false
				}, 25)
			}
		},

		isListVisible(visible) {
			// this is utilized for messages in meetings if the list is collapsed or not in view we should skip annimations on messages
			this.skipAnimation = !visible
		},

		activeHoverLockId(id) {
			console.debug('setting activeHoverLockId', id)
		},
	},

	computed: {
		showJumpToLatest() {
			return this.allowLoadForward || this.awayFromBottom
		},
		list() {
			return removeDuplicatesByField(this.messages.slice().reverse(), 'key')
		},
		isDM() {
			const id = this.chatId ?? this.store.currentChatId
			return getChat(id)?.isdm
		},
		isThread() {
			const id = this.chatId ?? this.store.currentChatId
			return getChat(id)?.is_thread
		},
	},

	methods: {
		// The list is laid out column-reverse, so DOM order runs bottom-to-top.
		// Native copy walks the DOM, which reverses multi-message selections and
		// pulls in timestamps and date dividers. Rebuild the payload instead.
		onCopy(e) {
			try {
				const listEl = this.$refs.list?.$el
				const selection = window.getSelection()
				if (!listEl || !selection?.rangeCount || selection.isCollapsed) return

				const range = selection.getRangeAt(0)
				const messages = Array.from(
					listEl.querySelectorAll('.message-outer-container')
				).filter((el) => range.intersectsNode(el))

				// a selection within one message already copies correctly
				if (messages.length < 2) return

				const text = messages
					.reverse()
					.map((el) => this.selectedTextWithin(el, range))
					.filter(Boolean)
					.join('\n')
				if (!text) return

				e.clipboardData.setData('text/plain', text)
				e.preventDefault()
			} catch (err) {
				// fall through to the browser's own copy rather than losing it
				console.warn('copy: falling back to native', err)
			}
		},

		// `.message-content` is the only part of a message that opts back into
		// user-select: text (MessageText.vue), so it is also the only part worth
		// copying. Cards, media, timestamps, dividers and avatars have none by
		// design, and stay out without needing to be listed here.
		selectedTextWithin(el, range) {
			return Array.from(el.querySelectorAll('.message-content'))
				.filter((node) => range.intersectsNode(node))
				.map((node) => this.clipToRange(node, range))
				.filter(Boolean)
				.join('\n')
		},

		// the part of `range` that falls inside `node`
		clipToRange(node, range) {
			const clipped = document.createRange()
			clipped.selectNodeContents(node)
			if (range.compareBoundaryPoints(Range.START_TO_START, clipped) > 0) {
				clipped.setStart(range.startContainer, range.startOffset)
			}
			if (range.compareBoundaryPoints(Range.END_TO_END, clipped) < 0) {
				clipped.setEnd(range.endContainer, range.endOffset)
			}
			return clipped.toString().trim()
		},

		hideTippy() {
			hideAll()
		},

		close() {
			if (this.clickToClose) this.$emit('close')
		},

		loadBackward() {
			if (!this.loadedBackward) {
				this.loadedBackward = true
				this.$emit('load-backward')
			}
		},

		loadForward() {
			if (!this.loadedForward) {
				this.loadedForward = true
				this.$emit('load-forward')
			}
		},

		jumpToLatest() {
			this.$emit('jump-to-latest')
		},

		checkScrollPosition() {
			if (this.$refs.list?.$el) {
				this.awayFromBottom =
					this.$refs.list.$el.scrollTop < JUMP_TO_LATEST_TOLERANCE
			}
		},

		handleScroll: debounce(function () {
			this.hideTippy()
			this.checkScrollPosition()
		}, 25),

		scrollToBottom() {
			this.$refs.list.$el.scrollTo(0, 0)
		},

		onMessageEdit(isEditing, index) {
			this.isEditing = isEditing
			this.trimActionsForMsgId = isEditing ? index - 1 : null
			if (isEditing) {
				setTimeout(() => {
					this.pauseAutoScroll()
				}, 100)
			}
		},

		pauseAutoScroll() {
			const { $el } = this.$refs.list
			if ($el?.scrollTop === 0) {
				// if we adjust the scroll position by just a few pixels away from 0,
				// then the list will stop auto-scrolling as new messages come in
				$el.scrollTop = -3
			}
		},

		isPinned(message_id) {
			if (!message_id) return false
			const chatId = this.chatId ?? this.store.currentChatId
			return getChat(chatId)?.pinned_message_id === message_id
		},

		lockHoverToMessage(id) {
			this.activeHoverLockId = id
		},

		clearHoverLock() {
			this.activeHoverLockId = null
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.message-list {
	flex-grow: 1;
	width: 100%;
	overflow: auto;
	overflow-x: hidden;
	display: flex;
	flex-direction: column-reverse;
	padding: 0 16px 30px 16px;
	// Contain the absolutely-positioned .loading-skeletons overlay AND
	// isolate the internal z-indexes (.message-container z:2,
	// thread connectors, etc.) so they don't escape and compete with
	// siblings like a shadowed header bar above the list.
	position: relative;
	z-index: 0;
	.loading-skeletons {
		@include absolute-cover;
		left: 20px;
		background: url('/images/common/chat-stream-skeleton-light.svg') repeat-y
			bottom left;
	}
	&.scroll-locked {
		overflow: hidden;
	}
	// Zero-height sticky anchor at the visual bottom of the column-reverse
	// scroller. The absolutely-positioned .jump-to-latest inside it anchors
	// to this instead of the scrolling content, so it floats above the
	// visible bottom edge rather than scrolling away with the messages.
	.jump-to-latest-anchor {
		position: sticky;
		bottom: 0;
		height: 0;
		flex-shrink: 0;
		z-index: 999;
	}
}

.retrieving-messages {
	text-align: center;
	font-family: Avenir-Medium;
	font-size: 14px;
	color: #c0c1c4;
	padding: 41px 16px;
	opacity: 0;
	transition: opacity 0.3s linear;
	&.loading {
		opacity: 1;
	}
}

.negative-space {
	flex: 1;
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.message-list {
		.loading-skeletons {
			background: url('/images/common/chat-stream-skeleton-dark.svg') repeat-y
				bottom left;
		}
	}
}
</style>
